import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  List,
  ListItem,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import TabDescription from './TabDescription';
import { useRegionContext } from '../../region/RegionContext';
import { queryAllRegions } from '../../utils/queryAllRegions';

function mergeConnected(integrationList, squareList) {
  var seen = {};
  var merged = [];
  integrationList.forEach(function (biz) { seen[biz.id] = true; merged.push(biz); });
  squareList.forEach(function (biz) {
    if (!seen[biz.id]) merged.push(biz);
  });
  return merged;
}

var PAGE_SIZE = 25;

var POSConnectionsTab = function () {
  var { db, isGlobal, allRegionDbs } = useRegionContext();
  var [innerTab, setInnerTab] = useState(0);
  var [requested, setRequested] = useState([]);
  var [connected, setConnected] = useState([]);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);
  var [reqPage, setReqPage] = useState(0);
  var [conPage, setConPage] = useState(0);

  useEffect(function () {
    loadData();
  }, [db, isGlobal, allRegionDbs]);

  var loadData = async function () {
    try {
      setLoading(true);
      setError(null);

      var requestedQuery = function (regionDb) {
        return regionDb.collection('requestedPOS').orderBy('requestedAt', 'desc').limit(200);
      };
      var integrationQuery = function (regionDb) {
        return regionDb.collection('Business').where('integration.status', '==', 'connected').limit(200);
      };
      var squareQuery = function (regionDb) {
        return regionDb.collection('Business').where('squareConfig.locationId', '>', '').limit(200);
      };

      var requestedData, connectedData;
      if (isGlobal && allRegionDbs) {
        var results = await Promise.all([
          queryAllRegions(allRegionDbs, null, requestedQuery),
          queryAllRegions(allRegionDbs, null, integrationQuery),
          queryAllRegions(allRegionDbs, null, squareQuery),
        ]);
        requestedData = results[0];
        connectedData = mergeConnected(results[1], results[2]);
      } else {
        var [reqSnap, intSnap, sqSnap] = await Promise.all([
          requestedQuery(db).get(),
          integrationQuery(db).get(),
          squareQuery(db).get(),
        ]);
        requestedData = reqSnap.docs.map(function (doc) { return { id: doc.id, ...doc.data() }; });
        var intData = intSnap.docs.map(function (doc) { return { id: doc.id, ...doc.data() }; });
        var sqData = sqSnap.docs.map(function (doc) { return { id: doc.id, ...doc.data() }; });
        connectedData = mergeConnected(intData, sqData);
      }

      requestedData.sort(function (a, b) {
        var aTime = a.requestedAt?.toDate?.()?.getTime?.() || 0;
        var bTime = b.requestedAt?.toDate?.()?.getTime?.() || 0;
        return bTime - aTime;
      });

      connectedData.sort(function (a, b) {
        var aTime = (a.integration?.connection?.connectedAt || a.squareConfig?.connectedAt)?.toDate?.()?.getTime?.() || 0;
        var bTime = (b.integration?.connection?.connectedAt || b.squareConfig?.connectedAt)?.toDate?.()?.getTime?.() || 0;
        return bTime - aTime;
      });

      setRequested(requestedData);
      setConnected(connectedData);
      setReqPage(0);
      setConPage(0);
    } catch (err) {
      console.error('Error loading POS connections:', err);
      setError('Failed to load POS connections');
    } finally {
      setLoading(false);
    }
  };

  var statusColor = function (status) {
    if (status === 'connected' || status === 'active') return 'success';
    if (status === 'pending') return 'warning';
    return 'default';
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading POS connections...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <TabDescription>
        POS integration status across all venues. "Requested" shows venues that asked for a POS
        connection (non-self-serve). "Connected" shows venues with an active POS integration.
      </TabDescription>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadData}
        >
          Refresh
        </Button>
      </Box>

      <Tabs
        value={innerTab}
        onChange={function (e, v) { setInnerTab(v); }}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={'Requested (' + requested.length + ')'} />
        <Tab label={'Connected (' + connected.length + ')'} />
      </Tabs>

      {innerTab === 0 && (
        requested.length === 0 ? (
          <Card>
            <CardContent>
              <Typography color="text.secondary" textAlign="center">
                No POS connection requests yet.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button variant="outlined" size="small" disabled={reqPage === 0} onClick={function () { setReqPage(function (p) { return p - 1; }); }}>
              Previous
            </Button>
            <Typography variant="body2">
              Page {reqPage + 1} of {Math.ceil(requested.length / PAGE_SIZE)} ({requested.length} total)
            </Typography>
            <Button variant="outlined" size="small" disabled={(reqPage + 1) * PAGE_SIZE >= requested.length} onClick={function () { setReqPage(function (p) { return p + 1; }); }}>
              Next
            </Button>
          </Box>
          <List disablePadding>
            {requested.slice(reqPage * PAGE_SIZE, (reqPage + 1) * PAGE_SIZE).map(function (req) {
              var posLabel = req.posName || req.posType || 'Unknown';
              return (
                <ListItem
                  key={req.id}
                  sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 1.5, px: 2 }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {req.bizName || req.businessId}
                      </Typography>
                      <Chip label={posLabel} size="small" variant="outlined" color="primary" sx={{ fontSize: 11 }} />
                      {req.provider && (
                        <Chip label={req.provider} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                      )}
                      <Chip
                        label={req.status || 'pending'}
                        size="small"
                        color={statusColor(req.status)}
                        sx={{ fontSize: 11 }}
                      />
                      {req._region && (
                        <Chip label={req._region} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                      )}
                    </Box>
                    {req.bizAddress && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {[req.bizAddress, req.bizSuburb, req.bizState].filter(Boolean).join(', ')}
                      </Typography>
                    )}
                    {req.userEmail && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Requested by: {req.userEmail}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2, whiteSpace: 'nowrap' }}>
                    {req.requestedAt?.toDate?.()?.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) || ''}
                  </Typography>
                </ListItem>
              );
            })}
          </List>
          </>
        )
      )}

      {innerTab === 1 && (
        connected.length === 0 ? (
          <Card>
            <CardContent>
              <Typography color="text.secondary" textAlign="center">
                No venues have connected a POS yet.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button variant="outlined" size="small" disabled={conPage === 0} onClick={function () { setConPage(function (p) { return p - 1; }); }}>
              Previous
            </Button>
            <Typography variant="body2">
              Page {conPage + 1} of {Math.ceil(connected.length / PAGE_SIZE)} ({connected.length} total)
            </Typography>
            <Button variant="outlined" size="small" disabled={(conPage + 1) * PAGE_SIZE >= connected.length} onClick={function () { setConPage(function (p) { return p + 1; }); }}>
              Next
            </Button>
          </Box>
          <List disablePadding>
            {connected.slice(conPage * PAGE_SIZE, (conPage + 1) * PAGE_SIZE).map(function (biz) {
              var integration = biz.integration || {};
              var connection = integration.connection || {};
              var platform = biz.squareConfig ? 'square' : (connection.platform || integration.platform || 'unknown');
              return (
                <ListItem
                  key={biz.id}
                  sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 1.5, px: 2 }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {biz.bizName || biz.id}
                      </Typography>
                      <Chip label={platform} size="small" color="info" sx={{ fontSize: 11 }} />
                      <Chip label="Connected" size="small" color="success" sx={{ fontSize: 11 }} />
                      {biz._region && (
                        <Chip label={biz._region} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                      )}
                    </Box>
                    {biz.bizAddress && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {biz.bizAddress}
                      </Typography>
                    )}
                    {biz.squareConfig?.locationName && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Location: {biz.squareConfig.locationName}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2, whiteSpace: 'nowrap' }}>
                    {connection.connectedAt?.toDate?.()?.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) || ''}
                  </Typography>
                </ListItem>
              );
            })}
          </List>
          </>
        )
      )}
    </Box>
  );
};

export default POSConnectionsTab;
